/** @typedef {{ id: string, pieceIndex: number, x: number, y: number, groupId?: string }} JigsawBoardPiece */

export function newPieceGroupId() {
    return `pg${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

export function areGridNeighbors(pieceIndexA, pieceIndexB, gridN) {
    const colA = pieceIndexA % gridN;
    const rowA = Math.floor(pieceIndexA / gridN);
    const colB = pieceIndexB % gridN;
    const rowB = Math.floor(pieceIndexB / gridN);
    const dc = Math.abs(colA - colB);
    const dr = Math.abs(rowA - rowB);
    return (dc === 1 && dr === 0) || (dc === 0 && dr === 1);
}

export function expectedRelativeOffset(pieceIndexA, pieceIndexB, cellPxN, gridN) {
    const colA = pieceIndexA % gridN;
    const rowA = Math.floor(pieceIndexA / gridN);
    const colB = pieceIndexB % gridN;
    const rowB = Math.floor(pieceIndexB / gridN);
    return {
        dx: (colA - colB) * cellPxN,
        dy: (rowA - rowB) * cellPxN,
    };
}

export function applyHomeSnap(nx, ny, pieceIndex, gridN, cellPxN, snapTol = cellPxN * 0.38) {
    const homeX = (pieceIndex % gridN) * cellPxN;
    const homeY = Math.floor(pieceIndex / gridN) * cellPxN;
    let x = nx;
    let y = ny;
    if (Math.abs(x - homeX) <= snapTol && Math.abs(y - homeY) <= snapTol) {
        x = homeX;
        y = homeY;
    }
    return { x, y, homeX, homeY };
}

export function isAtHome(x, y, homeX, homeY) {
    return x === homeX && y === homeY;
}

export function isPieceExactlyAtHome(b, gridN, cellPxN) {
    const homeX = (b.pieceIndex % gridN) * cellPxN;
    const homeY = Math.floor(b.pieceIndex / gridN) * cellPxN;
    const tol = Math.max(0.5, cellPxN * 0.02);
    return Math.abs(b.x - homeX) <= tol && Math.abs(b.y - homeY) <= tol;
}

/** True when the dropped piece just snapped to its exact home slot (not already there). */
export function didDroppedPieceLandAtHome(prev, next, droppedPieceIndex, gridN, cellPxN) {
    const wasHome = prev.some(
        (b) => b.pieceIndex === droppedPieceIndex && isPieceExactlyAtHome(b, gridN, cellPxN),
    );
    const placed = next.find((b) => b.pieceIndex === droppedPieceIndex);
    if (!placed) return false;
    return !wasHome && isPieceExactlyAtHome(placed, gridN, cellPxN);
}

/** pieceIndex → set of pieceIndexes glued to it (groups of one are not glued). */
function gluedTeammates(pieces) {
    const byGroup = new Map();
    for (const b of Array.isArray(pieces) ? pieces : []) {
        if (!b?.groupId) continue;
        const members = byGroup.get(b.groupId) ?? [];
        members.push(b);
        byGroup.set(b.groupId, members);
    }
    const teammates = new Map();
    for (const members of byGroup.values()) {
        if (members.length < 2) continue;
        for (const b of members) {
            teammates.set(
                b.pieceIndex,
                new Set(
                    members
                        .filter((other) => other.pieceIndex !== b.pieceIndex)
                        .map((other) => other.pieceIndex),
                ),
            );
        }
    }
    return teammates;
}

/**
 * Board ids of pieces that just gained a glued neighbour away from home — the whole cluster,
 * not only the dropped piece, so the effect reads as "these pieces are stuck together".
 */
export function findNewlyGluedPieceIds(prevPieces, nextPieces, gridN, cellPxN) {
    const before = gluedTeammates(prevPieces);
    const after = gluedTeammates(nextPieces);
    const ids = [];
    for (const piece of Array.isArray(nextPieces) ? nextPieces : []) {
        const mates = after.get(piece.pieceIndex);
        if (!mates || mates.size === 0) continue;
        if (isPieceExactlyAtHome(piece, gridN, cellPxN)) continue;
        const previousMates = before.get(piece.pieceIndex) ?? new Set();
        let gained = false;
        for (const mate of mates) {
            if (!previousMates.has(mate)) {
                gained = true;
                break;
            }
        }
        if (gained) ids.push(piece.id);
    }
    return ids;
}

function homeOccupancySet(pieces, gridN, cellPxN) {
    const atHome = new Set();
    if (!Array.isArray(pieces) || gridN < 1 || !(cellPxN > 0)) return atHome;
    for (const b of pieces) {
        if (b && isPieceExactlyAtHome(b, gridN, cellPxN)) {
            atHome.add(b.pieceIndex);
        }
    }
    return atHome;
}

function isRowCompleteAtHome(atHome, gs, row) {
    for (let c = 0; c < gs; c += 1) {
        if (!atHome.has(row * gs + c)) return false;
    }
    return true;
}

function isColCompleteAtHome(atHome, gs, col) {
    for (let r = 0; r < gs; r += 1) {
        if (!atHome.has(r * gs + col)) return false;
    }
    return true;
}

/**
 * Newly completed rows/columns of pieces snapped to home.
 * Skips full-board solve (SuccessCard handles that).
 */
export function findJigsawLineMatchCelebration(prevPieces, nextPieces, gridN, cellPxN) {
    if (gridN < 1 || !(cellPxN > 0)) return null;
    const total = gridN * gridN;
    const nextHome = homeOccupancySet(nextPieces, gridN, cellPxN);
    if (nextHome.size >= total) return null;

    const prevHome = homeOccupancySet(prevPieces, gridN, cellPxN);
    const rows = [];
    const cols = [];
    for (let r = 0; r < gridN; r += 1) {
        if (isRowCompleteAtHome(nextHome, gridN, r) && !isRowCompleteAtHome(prevHome, gridN, r)) {
            rows.push(r);
        }
    }
    for (let c = 0; c < gridN; c += 1) {
        if (isColCompleteAtHome(nextHome, gridN, c) && !isColCompleteAtHome(prevHome, gridN, c)) {
            cols.push(c);
        }
    }
    if (rows.length === 0 && cols.length === 0) return null;

    const cells = [];
    const seen = new Set();
    for (const r of rows) {
        for (let c = 0; c < gridN; c += 1) {
            const i = r * gridN + c;
            if (!seen.has(i)) {
                seen.add(i);
                cells.push(i);
            }
        }
    }
    for (const c of cols) {
        for (let r = 0; r < gridN; r += 1) {
            const i = r * gridN + c;
            if (!seen.has(i)) {
                seen.add(i);
                cells.push(i);
            }
        }
    }
    return { rows, cols, cells };
}

export function isCorrectlyAdjacent(pieceA, pieceB, cellPxN, gridN, tolerance) {
    if (!areGridNeighbors(pieceA.pieceIndex, pieceB.pieceIndex, gridN)) {
        return false;
    }
    const { dx, dy } = expectedRelativeOffset(
        pieceA.pieceIndex,
        pieceB.pieceIndex,
        cellPxN,
        gridN,
    );
    return (
        Math.abs(pieceA.x - pieceB.x - dx) <= tolerance
        && Math.abs(pieceA.y - pieceB.y - dy) <= tolerance
    );
}

function alignPieceToAnchor(moving, anchor, gridN, cellPxN) {
    const { dx, dy } = expectedRelativeOffset(
        moving.pieceIndex,
        anchor.pieceIndex,
        cellPxN,
        gridN,
    );
    return { ...moving, x: anchor.x + dx, y: anchor.y + dy };
}

function groupSize(pieces, piece) {
    if (!piece?.groupId) return 1;
    let n = 0;
    for (const b of pieces) {
        if (b.groupId === piece.groupId) n += 1;
    }
    return Math.max(1, n);
}

function mergeTwoPieces(pieces, anchorId, otherId, gridN, cellPxN) {
    const anchor = pieces.find((b) => b.id === anchorId);
    const other = pieces.find((b) => b.id === otherId);
    if (!anchor || !other) return pieces;
    if (anchor.groupId && other.groupId && anchor.groupId === other.groupId) {
        return pieces;
    }

    // Keep the larger cluster fixed; translate the whole other cluster so relative
    // offsets stay correct (solo-as-anchor used to yank one neighbor out of a chain).
    const anchorSize = groupSize(pieces, anchor);
    const otherSize = groupSize(pieces, other);
    const fixed = otherSize > anchorSize ? other : anchor;
    const moving = fixed.id === anchor.id ? other : anchor;

    const mergedGroupId = fixed.groupId ?? moving.groupId ?? newPieceGroupId();
    const alignedMoving = alignPieceToAnchor(moving, fixed, gridN, cellPxN);
    const deltaX = alignedMoving.x - moving.x;
    const deltaY = alignedMoving.y - moving.y;
    const movingGroupId = moving.groupId;
    const fixedGroupId = fixed.groupId;

    return pieces.map((b) => {
        const inMoving =
            b.id === moving.id || (movingGroupId && b.groupId === movingGroupId);
        if (inMoving) {
            return {
                ...b,
                x: b.x + deltaX,
                y: b.y + deltaY,
                groupId: mergedGroupId,
            };
        }
        const inFixed = b.id === fixed.id || (fixedGroupId && b.groupId === fixedGroupId);
        if (inFixed) {
            return { ...b, groupId: mergedGroupId };
        }
        return b;
    });
}

/**
 * When pieces are close to grid neighbors in the correct relative position, merge into groups.
 * Any orthogonal neighbor pair can connect (no limit on chain length or which end).
 */
export function tryAttachNeighbors(boardPieces, _anchorBoardId, gridN, cellPxN) {
    const tolerance = cellPxN * 0.45;
    let pieces = boardPieces.map((b) => ({ ...b }));
    let attached = false;

    for (let pass = 0; pass < pieces.length; pass += 1) {
        let mergedThisPass = false;
        for (let i = 0; i < pieces.length; i += 1) {
            for (let j = i + 1; j < pieces.length; j += 1) {
                if (
                    pieces[i].groupId
                    && pieces[j].groupId
                    && pieces[i].groupId === pieces[j].groupId
                ) {
                    continue;
                }
                if (isCorrectlyAdjacent(pieces[i], pieces[j], cellPxN, gridN, tolerance)) {
                    pieces = mergeTwoPieces(pieces, pieces[i].id, pieces[j].id, gridN, cellPxN);
                    attached = true;
                    mergedThisPass = true;
                    break;
                }
            }
            if (mergedThisPass) break;
        }
        if (!mergedThisPass) break;
    }

    return { pieces, attached };
}

function getGroupMembers(boardPieces, groupId) {
    if (!groupId) return [];
    return boardPieces.filter((b) => b.groupId === groupId);
}

function isPieceNearHome(piece, gridN, cellPxN, snapTol) {
    const homeX = (piece.pieceIndex % gridN) * cellPxN;
    const homeY = Math.floor(piece.pieceIndex / gridN) * cellPxN;
    return Math.abs(piece.x - homeX) <= snapTol && Math.abs(piece.y - homeY) <= snapTol;
}

function snapPieceToHome(piece, gridN, cellPxN) {
    const homeX = (piece.pieceIndex % gridN) * cellPxN;
    const homeY = Math.floor(piece.pieceIndex / gridN) * cellPxN;
    return { ...piece, x: homeX, y: homeY };
}

/**
 * Move a solo piece or an entire attached group.
 * @param {{ snapHome?: boolean }} [options] When {@code snapHome} is false, only translates
 *   (call {@link snapMovedPiecesToHome} after {@link tryAttachNeighbors}).
 */
export function moveBoardPieceGroup(boardPieces, anchorBoardId, nx, ny, gridN, cellPxN, options = {}) {
    const snapHome = options.snapHome !== false;
    const snapTol = cellPxN * 0.38;
    const anchor = boardPieces.find((b) => b.id === anchorBoardId);
    if (!anchor) {
        return { pieces: boardPieces, anchorSnap: null };
    }

    const groupId = anchor.groupId;
    const groupMembers = getGroupMembers(boardPieces, groupId);
    const deltaX = nx - anchor.x;
    const deltaY = ny - anchor.y;

    let moved = boardPieces.map((b) => {
        const inGroup = Boolean(groupId && b.groupId === groupId);
        if (!inGroup && b.id !== anchorBoardId) {
            return b;
        }
        return { ...b, x: b.x + deltaX, y: b.y + deltaY };
    });

    if (!snapHome) {
        return { pieces: moved, anchorSnap: null };
    }

    if (groupMembers.length > 1) {
        const tentativeGroup = moved.filter((b) => b.groupId === groupId);
        const allNearHome = tentativeGroup.every((b) =>
            isPieceNearHome(b, gridN, cellPxN, snapTol),
        );
        if (allNearHome) {
            moved = moved.map((b) =>
                b.groupId === groupId ? snapPieceToHome(b, gridN, cellPxN) : b,
            );
        }
        const updatedAnchor = moved.find((b) => b.id === anchorBoardId) ?? anchor;
        const anchorSnap = applyHomeSnap(
            updatedAnchor.x,
            updatedAnchor.y,
            updatedAnchor.pieceIndex,
            gridN,
            cellPxN,
            snapTol,
        );
        return { pieces: moved, anchorSnap };
    }

    const snapped = applyHomeSnap(nx, ny, anchor.pieceIndex, gridN, cellPxN, snapTol);
    return {
        pieces: boardPieces.map((b) =>
            b.id === anchorBoardId ? { ...b, x: snapped.x, y: snapped.y } : b,
        ),
        anchorSnap: snapped,
    };
}

/** Home-snap solos / whole groups after attach so connect is not undone by snap. */
export function snapMovedPiecesToHome(boardPieces, gridN, cellPxN) {
    const snapTol = cellPxN * 0.38;
    const groups = new Map();
    for (const b of boardPieces) {
        if (!b.groupId) continue;
        const list = groups.get(b.groupId) || [];
        list.push(b.id);
        groups.set(b.groupId, list);
    }

    const snapGroupIds = new Set();
    for (const [gid, ids] of groups) {
        if (ids.length < 2) continue;
        const members = boardPieces.filter((b) => b.groupId === gid);
        if (members.every((b) => isPieceNearHome(b, gridN, cellPxN, snapTol))) {
            snapGroupIds.add(gid);
        }
    }

    return boardPieces.map((b) => {
        if (b.groupId && snapGroupIds.has(b.groupId)) {
            return snapPieceToHome(b, gridN, cellPxN);
        }
        if (!b.groupId || (groups.get(b.groupId)?.length ?? 0) <= 1) {
            const snapped = applyHomeSnap(b.x, b.y, b.pieceIndex, gridN, cellPxN, snapTol);
            return { ...b, x: snapped.x, y: snapped.y };
        }
        return b;
    });
}

/**
 * Place a new board piece and attach to nearby neighbors when aligned.
 */
export function placeBoardPieceWithAttach(boardPieces, newPiece, gridN, cellPxN) {
    const withNew = [...boardPieces, newPiece];
    const { pieces, attached } = tryAttachNeighbors(withNew, newPiece.id, gridN, cellPxN);
    return { pieces: snapMovedPiecesToHome(pieces, gridN, cellPxN), attached };
}
